import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  snackbar,
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
  useConfirmDialog,
} from '@occc-app/design-system';
import { useDisplayCategoryDeleteMutation } from '@occc-app/dataAccessLayer/displayCategory/mutations';
import { DisplayCategoryFragment } from '@occc/graphql';

import { routeCreator } from '../utils';

type DisplayCategoryTableRowActionsProps = {
  displayCategory: DisplayCategoryFragment;
  isUserPermittedToEditDisplayCategory: boolean;
  isUserPermittedToDeleteDisplayCategory: boolean;
};

export const DisplayCategoryTableRowActions = ({
  displayCategory,
  isUserPermittedToEditDisplayCategory,
  isUserPermittedToDeleteDisplayCategory,
}: DisplayCategoryTableRowActionsProps) => {
  const navigate = useNavigate();

  const onDeleteCompleted = () =>
    snackbar.success('Display Category has been successfully deleted.');

  const [displayCategoryDelete] = useDisplayCategoryDeleteMutation({
    onCompleted: onDeleteCompleted,
    refetchQueries: ['displayCategories'],
  });
  const onDelete = () => displayCategoryDelete({ variables: { id: displayCategory.id } });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isUserPermittedToEditDisplayCategory) {
      items.push({
        label: 'Edit Display Category',
        onClick: () => navigate(routeCreator.update(displayCategory.id)),
        dataTestId: 'edit',
      });
    }

    if (isUserPermittedToDeleteDisplayCategory) {
      items.push({
        label: 'Delete Display Category',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [
    navigate,
    displayCategory.id,
    setOpen,
    isUserPermittedToEditDisplayCategory,
    isUserPermittedToDeleteDisplayCategory,
  ]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>
          This action will delete the Display Category "{displayCategory.name}".
        </Typography>
      </ConfirmDialog>
    </>
  );
};
